import type {
  FleetDevice,
  FleetUser,
} from 'src/api/endpoints/cloudEnterprise.types';
import type { FleetRoleType } from 'src/api/endpoints/cloudEnterpriseAdminApi.types';
import { deviceHelpers } from 'src/utils/deviceHelpers';

export const getName = (user: FleetUser): string => {
  const firstName = user.firstName ?? '';
  const lastName = user.lastName ?? '';
  return [firstName, lastName].filter(Boolean).join(' ') || user.name || '';
};

export const hasName = (user: FleetUser): boolean => {
  return !!user.firstName || !!user.lastName || !!user.name;
};

export const getEmail = (user: FleetUser): string => {
  return user.email ?? '';
};

export const getStatusLabel = (status: FleetUser['status']): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'deactivated':
      return 'Deactivated';
    case 'deletion_mark':
      return 'Marked for deletion';
    default:
      return 'Active';
  }
};

export const getUserStatusLabel = (user: FleetUser): string =>
  user.mdmDisabled ? 'MDM deactivated' : getStatusLabel(user.status);

export const sortRoles = (a: { name: string }, b: { name: string }) => {
  const orderByRoleType = [
    'OWNER',
    'ADMIN',
    'VIEWER',
    'UNKNOWN',
  ] as FleetRoleType[];
  // check the roles includes the keywords
  return (
    orderByRoleType.findIndex((r) => getRoleType(a.name) === r) -
    orderByRoleType.findIndex((r) => getRoleType(b.name) === r)
  );
};

export const getRoleType = (name = ''): FleetRoleType => {
  const lowerCaseName = name.toLowerCase();

  switch (lowerCaseName) {
    case 'enterprise owner':
    case 'owner':
      return 'OWNER';
    case 'enterprise admin':
    case 'admin':
      return 'ADMIN';
    case 'enterprise device viewer':
    case 'device viewer':
    case 'viewer':
      return 'VIEWER';
    default:
      return 'UNKNOWN';
  }
};

export const getRoleName = (role: { name?: string }) => {
  switch (getRoleType(role.name)) {
    case 'OWNER':
      return 'Owner';
    case 'ADMIN':
      return 'Admin';
    case 'VIEWER':
      return 'Viewer';
    default:
      return role.name || 'Unknown Role';
  }
};

export const getRoleDescription = (role: { name?: string }): string => {
  switch (getRoleType(role.name)) {
    case 'OWNER':
      return 'Can manage users, devices, settings and billing';
    case 'ADMIN':
      return 'Can manage users, devices and settings';
    case 'VIEWER':
      return 'Can view users and devices';
    default:
      return '';
  }
};

export const getDeviceComplianceStatus = (device: FleetDevice): string => {
  const status = device.policyCompliance?.status || 'unknown';

  switch (status) {
    case 'compliant':
      return 'Compliant';
    case 'nonCompliant':
      return 'Non-compliant';
    case 'notApplicable':
      return 'Not applicable';
    case 'unknown':
    default:
      return 'Unavailable';
  }
};

export const getDeviceVerificationStatus = (device: FleetDevice) => {
  switch (device.verified) {
    case 'verified': {
      if (deviceHelpers.isLegacySecurityDevice(device)) {
        return 'Confirmed';
      }
      return 'Verified';
    }
    case 'notVerified':
      return 'Not verified';
    default:
      return 'Unavailable';
  }
};

export const fleetUserHelpers = {
  getName,
  hasName,
  getEmail,
  getStatusLabel,
  getUserStatusLabel,
  getDeviceComplianceStatus,
  getDeviceVerificationStatus,
  getRoleName,
  getRoleDescription,
  getRoleType,
  sortRoles,
};
